import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Carousel as AntCarousel } from 'antd';
import cn from '../utils/cn';
import IconButton from '../IconButton';
import Dots from '../Dots';
import { ChevLeftIcon, ChevRightIcon } from '../icons';
import { resolveResponsiveValue } from '../breakpoints';
import useBreakpoint from '../../../hooks/useBreakpoint';
import styles from './Carousel.module.css';

const Carousel = React.forwardRef(function Carousel(
  {
    children,
    header,
    arrows = true,
    dots = true,
    infinite = false,
    slidesToShow = 1,
    gap = 0,
    autoplay = false,
    autoplaySpeed = 3000,
    afterChange,
    prevLabel = 'Previous',
    nextLabel = 'Next',
    controlsPlacement = 'split',
    keepControls = false,
    className,
    ...rest
  },
  ref
) {
  const instanceRef = useRef(null);
  const [index, setIndex] = useState(0);
  const { width } = useBreakpoint();

  const setRefs = node => {
    instanceRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };

  const resolvedSlidesToShow = resolveResponsiveValue(slidesToShow, width) || 1;
  const count = React.Children.count(children);
  const canLoop = count > resolvedSlidesToShow;
  const effInfinite = infinite && canLoop;
  const effAutoplay = autoplay && canLoop;
  const pages = effInfinite
    ? count
    : Math.max(1, count - resolvedSlidesToShow + 1);
  const atStart = !effInfinite && index <= 0;
  const atEnd = !effInfinite && index >= pages - 1;
  const showNav = count > 1;
  const renderNav = showNav || keepControls;

  useEffect(() => {
    if (index > pages - 1 && instanceRef.current) {
      instanceRef.current.goTo(pages - 1, true);
    }
  }, [index, pages]);

  const handleAfterChange = current => {
    setIndex(current);
    if (afterChange) afterChange(current);
  };
  const goPrev = () => instanceRef.current && instanceRef.current.prev();
  const goNext = () => instanceRef.current && instanceRef.current.next();
  const goTo = i => instanceRef.current && instanceRef.current.goTo(i);

  return (
    <div
      className={cn(styles.root, className)}
      style={gap ? { '--ui-carousel-gap': `${gap}px` } : undefined}>
      {controlsPlacement !== 'foot' && (header || (arrows && renderNav)) && (
        <div className={styles.head}>
          <div className={styles.header}>{header}</div>
          {arrows && renderNav && (
            <div className={styles.arrows}>
              <IconButton
                variant='nav'
                aria-label={prevLabel}
                onClick={goPrev}
                disabled={atStart}>
                <ChevLeftIcon
                  size={18}
                  style={{
                    color: atStart
                      ? 'var(--ui-text-soft)'
                      : 'var(--ui-blue-link)'
                  }}
                />
              </IconButton>
              <IconButton
                variant='nav'
                aria-label={nextLabel}
                onClick={goNext}
                disabled={atEnd}>
                <ChevRightIcon
                  size={18}
                  style={{
                    color: atEnd ? 'var(--ui-text-soft)' : 'var(--ui-blue-link)'
                  }}
                />
              </IconButton>
            </div>
          )}
        </div>
      )}

      <AntCarousel
        ref={setRefs}
        dots={false}
        infinite={effInfinite}
        slidesToShow={resolvedSlidesToShow}
        autoplay={effAutoplay}
        autoplaySpeed={autoplaySpeed}
        afterChange={handleAfterChange}
        {...rest}>
        {children}
      </AntCarousel>

      {controlsPlacement !== 'foot' && dots && showNav && (
        <div className={styles.dots}>
          <Dots
            total={pages}
            active={Math.min(index, pages - 1)}
            onSelect={goTo}
          />
        </div>
      )}

      {controlsPlacement === 'foot' && (dots || arrows) && renderNav && (
        <div className={styles.foot}>
          <div className={styles.footDots}>
            {dots && showNav && (
              <Dots
                total={pages}
                active={Math.min(index, pages - 1)}
                onSelect={goTo}
              />
            )}
          </div>
          {arrows && (
            <div className={styles.footArrows}>
              <IconButton
                variant='nav'
                aria-label={prevLabel}
                onClick={goPrev}
                disabled={atStart}>
                <ChevLeftIcon
                  size={18}
                  style={{
                    color: atStart
                      ? 'var(--ui-text-soft)'
                      : 'var(--ui-blue-link)'
                  }}
                />
              </IconButton>
              <IconButton
                variant='nav'
                aria-label={nextLabel}
                onClick={goNext}
                disabled={atEnd}>
                <ChevRightIcon
                  size={18}
                  style={{
                    color: atEnd ? 'var(--ui-text-soft)' : 'var(--ui-blue-link)'
                  }}
                />
              </IconButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

Carousel.propTypes = {
  children: PropTypes.node,
  header: PropTypes.node,
  arrows: PropTypes.bool,
  dots: PropTypes.bool,
  infinite: PropTypes.bool,
  slidesToShow: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      base: PropTypes.number.isRequired,
      xs: PropTypes.number,
      sm: PropTypes.number,
      md: PropTypes.number,
      lg: PropTypes.number,
      xl: PropTypes.number,
      xxl: PropTypes.number
    })
  ]),
  gap: PropTypes.number,
  autoplay: PropTypes.bool,
  autoplaySpeed: PropTypes.number,
  afterChange: PropTypes.func,
  prevLabel: PropTypes.string,
  nextLabel: PropTypes.string,
  controlsPlacement: PropTypes.oneOf(['split', 'foot']),
  keepControls: PropTypes.bool,
  className: PropTypes.string
};

export default Carousel;
